// FinKloz — Consolidated Journal Entries for Bank Reconciliation
// Shows all proposed JEs across all bank accounts in one view.

import React, { useMemo } from 'react';
import { sans, mono, green, greenLight, greenDark, red, amber, amberLight, useTheme } from '../tokens';
import useBankRecState from '../hooks/useBankRecState';
import { BANK_ACCOUNTS } from '../services/bankStatementParser';

const teal = '#0D9488';

function fmt$(n) {
  if (n === null || n === undefined || isNaN(n)) return '--';
  if (n === 0) return '--';
  const abs = Math.abs(n);
  const str = abs.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `$(${str})` : `$${str}`;
}

export default function BankRecJournalEntries({ companyId, month }) {
  const t = useTheme();
  const { state } = useBankRecState(companyId, month);

  // Collect all JEs across all accounts
  const allJEs = useMemo(() => {
    const jes = [];
    for (const acct of BANK_ACCOUNTS) {
      const data = state.accounts[acct.accountNo];
      if (!data) continue;

      // Proposed JEs from reconciliation
      for (const je of (data.proposedJEs || [])) {
        jes.push({ ...je, accountNo: acct.accountNo, accountName: acct.name, source: 'reconciliation' });
      }

      // FX JE from USD accounts
      if (acct.currency === 'USD' && data.fxConversion && data.fxConversion.fxJEAmount !== 0) {
        jes.push({
          id: `fx-${acct.accountNo}`,
          date: month + '-31',
          description: `FX revaluation - ${acct.name}`,
          debitAccount: data.fxConversion.fxJEAmount > 0 ? acct.accountNo : '74000',
          creditAccount: data.fxConversion.fxJEAmount > 0 ? '74000' : acct.accountNo,
          amount: Math.abs(data.fxConversion.fxJEAmount),
          type: 'fx',
          approved: false,
          accountNo: acct.accountNo,
          accountName: acct.name,
          source: 'fx_conversion',
        });
      }
    }
    return jes;
  }, [state, month]);

  const totalDebit = allJEs.reduce((s, je) => s + (je.amount || 0), 0);
  const approvedCount = allJEs.filter(je => je.approved).length;

  if (allJEs.length === 0) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ fontFamily: sans, fontSize: 14, color: t.textMuted }}>
          No journal entries to post.
        </div>
        <div style={{ fontFamily: sans, fontSize: 12, color: t.textFaint, marginTop: 6 }}>
          Complete reconciliation on each account to generate proposed JEs.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: sans, fontSize: 15, fontWeight: 700, color: t.textPrimary }}>
            Journal Entries
          </div>
          <div style={{ fontFamily: sans, fontSize: 11, color: t.textSecondary, marginTop: 2 }}>
            {allJEs.length} entries across all accounts -- {approvedCount} approved
          </div>
        </div>
        <button
          disabled
          title="Available when connected to Sage Intacct"
          style={{
            fontFamily: sans, fontSize: 12, fontWeight: 600,
            padding: '8px 16px', borderRadius: 6,
            border: `1px solid ${t.border}`, background: t.bgSurface,
            color: t.textMuted, cursor: 'not-allowed',
          }}
        >
          Post All to Sage
        </button>
      </div>

      {/* Header */}
      <div style={{
        display: 'grid', gridTemplateColumns: '80px 1fr 70px 70px 90px 60px',
        gap: 8, padding: '6px 0',
        fontFamily: sans, fontSize: 10, fontWeight: 600, color: t.textMuted,
        borderBottom: `2px solid ${t.border}`,
      }}>
        <span>Date</span>
        <span>Description</span>
        <span>Debit Acct</span>
        <span>Credit Acct</span>
        <span style={{ textAlign: 'right' }}>Amount</span>
        <span style={{ textAlign: 'center' }}>Source</span>
      </div>

      {/* JE rows */}
      {allJEs.map((je, i) => (
        <div key={je.id || i} style={{
          display: 'grid', gridTemplateColumns: '80px 1fr 70px 70px 90px 60px',
          gap: 8, padding: '7px 0', alignItems: 'center',
          borderBottom: `1px solid ${t.borderLight}`,
        }}>
          <span style={{ fontFamily: mono, fontSize: 11, color: t.textSecondary }}>{je.date}</span>
          <div>
            <div style={{ fontFamily: mono, fontSize: 11, color: t.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {je.description}
            </div>
            <div style={{ fontFamily: sans, fontSize: 9, color: t.textMuted }}>{je.accountName}</div>
          </div>
          <span style={{ fontFamily: mono, fontSize: 11, color: t.textSecondary }}>{je.debitAccount}</span>
          <span style={{ fontFamily: mono, fontSize: 11, color: t.textSecondary }}>{je.creditAccount}</span>
          <span style={{ fontFamily: mono, fontSize: 11, color: t.textPrimary, textAlign: 'right' }}>
            {fmt$(je.amount)}
          </span>
          <span style={{
            fontFamily: sans, fontSize: 9, fontWeight: 600, textAlign: 'center',
            padding: '2px 4px', borderRadius: 3,
            background: je.source === 'fx_conversion' ? '#EDE9FE' : amberLight,
            color: je.source === 'fx_conversion' ? '#7C3AED' : amber,
          }}>
            {je.source === 'fx_conversion' ? 'FX' : je.type === 'manual' ? 'Manual' : 'Auto'}
          </span>
        </div>
      ))}

      {/* Totals */}
      <div style={{
        display: 'grid', gridTemplateColumns: '80px 1fr 70px 70px 90px 60px',
        gap: 8, padding: '8px 0', marginTop: 4,
        borderTop: `2px solid ${t.border}`,
        fontFamily: mono, fontSize: 12, fontWeight: 700,
      }}>
        <span></span>
        <span style={{ color: t.textPrimary }}>TOTAL</span>
        <span></span>
        <span></span>
        <span style={{ color: t.textPrimary, textAlign: 'right' }}>{fmt$(totalDebit)}</span>
        <span></span>
      </div>
    </div>
  );
}
